import {
  ApiError,
  deliveryAction,
  getMessageRecipients,
  retryFailed,
  type Recipient,
  type SentMessageDetail,
} from "@/api";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  DeliveryStatsBar,
  DeliveryStatsLegend,
  failedCount,
  formatPercent,
} from "@/components/ui/DeliveryStatsBar";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/format";
import { EVENT_TYPE_LABEL, MESSAGE_PHASE_LABEL, bounceCategoryLabel, typeTone } from "@/lib/labels";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

type Kind = "delivered" | "bounced" | "failed" | "open";
type Filter = "all" | Kind;

type SortKey = "address" | "domain" | "list" | "language" | "state" | "time" | "reason";
type SortDir = "asc" | "desc";
type GroupKey = "none" | "list" | "domain" | "state" | "language" | "code" | "bounce";

const PAGE = 200;
const SORT_STORE = "edicta.recipients.sort";
const GROUP_STORE = "edicta.recipients.group";

const FILTERS: { id: Filter; label: string }[] = [
  { id: "all", label: "Alle" },
  { id: "delivered", label: "Zugestellt" },
  { id: "failed", label: "Fehlgeschlagen" },
  { id: "bounced", label: "Rückläufer" },
  { id: "open", label: "Offen" },
];

const STATE_VIEW: Record<string, { label: string; dot: string }> = {
  delivered: { label: "zugestellt", dot: "bg-green" },
  bounced: { label: "Rückläufer", dot: "bg-amber" },
  rejected: { label: "abgelehnt (5xx)", dot: "bg-danger" },
  failed: { label: "fehlgeschlagen", dot: "bg-danger" },
  unknown: { label: "unbekannt", dot: "bg-danger opacity-40" },
  open: { label: "offen", dot: "bg-accent" },
};

const DEFER_REASON_LABEL: Record<string, string> = {
  rate_limit: "Rate-Limit",
  scheduled: "geplant",
  retry_backoff: "wartet auf neuen Versuch",
  smtp_unavailable: "Mailserver nicht erreichbar",
};

const COLUMNS: { key: SortKey; label: string }[] = [
  { key: "address", label: "Adresse" },
  { key: "domain", label: "Domain" },
  { key: "list", label: "Liste" },
  { key: "language", label: "Sprache" },
  { key: "state", label: "Status" },
  { key: "time", label: "Zeit" },
  { key: "reason", label: "Serverantwort / Grund" },
];

const GROUPS: { id: GroupKey; label: string }[] = [
  { id: "none", label: "nicht gruppieren" },
  { id: "list", label: "Liste" },
  { id: "domain", label: "Domain" },
  { id: "state", label: "Status" },
  { id: "code", label: "Antwortcode" },
  { id: "bounce", label: "Bounce-Kategorie" },
  { id: "language", label: "Sprache" },
];

// Status order for sorting and grouping: what needs attention first.
const STATE_RANK = ["rejected", "failed", "unknown", "bounced", "open", "delivered"];

const collator = new Intl.Collator("de", { numeric: true, sensitivity: "base" });

const fmt = (n: number) => n.toLocaleString("de-DE");

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = window.localStorage.getItem(key);
    return value && (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
  } catch {
    return fallback;
  }
}

function store(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Only a convenience.
  }
}

function kindOf(r: Recipient): Kind {
  if (r.bounced_at) return "bounced";
  if (r.state === "submitted") return "delivered";
  if (r.state === "rejected" || r.state === "failed" || r.state === "unknown") return "failed";
  return "open";
}

function stateView(r: Recipient) {
  const kind = kindOf(r);
  return kind === "failed" ? STATE_VIEW[r.state] : STATE_VIEW[kind];
}

function timeOf(r: Recipient): string | null {
  switch (kindOf(r)) {
    case "delivered":
      return r.submitted_at;
    case "bounced":
      return r.bounced_at;
    case "failed":
      return r.finished_at;
    default:
      return r.next_attempt_at;
  }
}

function reasonOf(r: Recipient): string {
  switch (kindOf(r)) {
    case "delivered":
      return r.smtp_response ?? "";
    case "bounced":
      return [r.bounce_status, r.bounce_diagnostic].filter(Boolean).join(" ") || "Rückläufer ohne Details";
    case "failed":
      return r.last_error || r.smtp_response || "";
    default: {
      const reason = r.defer_reason ? (DEFER_REASON_LABEL[r.defer_reason] ?? r.defer_reason) : "wartet";
      return r.last_error ? `${reason} · ${r.last_error}` : reason;
    }
  }
}

function stateKey(r: Recipient): string {
  const kind = kindOf(r);
  return kind === "failed" ? r.state : kind;
}

// "550 5.1.1 …" → "550 5.1.1"; plain "250 OK …" → "250".
function codeOf(r: Recipient): string {
  const match = /^(\d{3})(?:[ -](\d\.\d{1,3}\.\d{1,3}))?/.exec(reasonOf(r).trim());
  if (!match) return "";
  return match[2] ? `${match[1]} ${match[2]}` : match[1];
}

function sortValue(r: Recipient, key: SortKey): string | number | null {
  switch (key) {
    case "address":
      return r.address;
    case "domain":
      return r.domain || null;
    case "list":
      return r.list_id_short;
    case "language":
      return r.language || null;
    case "state":
      return STATE_RANK.indexOf(stateKey(r));
    case "time": {
      const when = timeOf(r);
      return when ? Date.parse(when) : null;
    }
    case "reason":
      return reasonOf(r) || null;
  }
}

function compareValues(a: string | number | null, b: string | number | null): number {
  if (a === b) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return collator.compare(String(a), String(b));
}

function groupOf(r: Recipient, key: GroupKey): { id: string; label: string; rank: number | string } {
  switch (key) {
    case "list":
      return { id: r.list_id_short ?? "", label: r.list_id_short ?? "ohne Liste", rank: r.list_id_short ?? "\uffff" };
    case "domain":
      return { id: r.domain, label: r.domain || "ohne Domain", rank: 0 };
    case "state": {
      const state = stateKey(r);
      return { id: state, label: STATE_VIEW[state]?.label ?? state, rank: STATE_RANK.indexOf(state) };
    }
    case "code": {
      const code = codeOf(r);
      return { id: code, label: code || "ohne Code", rank: 0 };
    }
    case "bounce": {
      const id = r.bounced_at ? (r.bounce_category ?? "") : "-";
      return { id, label: r.bounced_at ? bounceCategoryLabel(r.bounce_category) : "kein Rückläufer", rank: r.bounced_at ? 0 : 1 };
    }
    case "language":
      return { id: r.language, label: r.language ? r.language.toUpperCase() : "—", rank: r.language || "\uffff" };
    default:
      return { id: "", label: "", rank: 0 };
  }
}

type Item =
  | { kind: "group"; id: string; label: string; count: number; collapsed: boolean }
  | { kind: "row"; r: Recipient };

function csvCell(value: string): string {
  return /[";\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

function downloadCsv(rows: Recipient[], name: string) {
  const header = ["Adresse", "Domain", "Liste", "Sprache", "Status", "Bounce-Kategorie", "Zeit", "Grund"];
  const lines = rows.map((r) =>
    [
      r.address,
      r.domain,
      r.list_id_short ?? "",
      r.language,
      stateView(r).label,
      r.bounced_at ? bounceCategoryLabel(r.bounce_category) : "",
      timeOf(r) ?? "",
      reasonOf(r),
    ]
      .map(csvCell)
      .join(";"),
  );
  const blob = new Blob([`﻿${[header.join(";"), ...lines].join("\n")}`], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

export function SentMessagePage() {
  const { id = "" } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<SentMessageDetail | null>(null);
  const [filter, setFilter] = useState<Filter | null>(null);
  const [query, setQuery] = useState("");
  const [listFilter, setListFilter] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>(() =>
    readStored(SORT_STORE, COLUMNS.map((c) => c.key), "address"),
  );
  const [sortDir, setSortDir] = useState<SortDir>(() =>
    readStored(`${SORT_STORE}.dir`, ["asc", "desc"] as const, "asc"),
  );
  const [groupBy, setGroupBy] = useState<GroupKey>(() =>
    readStored(GROUP_STORE, GROUPS.map((g) => g.id), "none"),
  );
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [shown, setShown] = useState(PAGE);
  const scroller = useRef<HTMLDivElement>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setDetail(await getMessageRecipients(id));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Mail konnte nicht geladen werden.");
    }
  }, [id]);

  useEffect(() => {
    setDetail(null);
    setFilter(null);
    setListFilter(null);
    void load();
  }, [load]);

  const sending = detail?.message.state === "sending";
  useEffect(() => {
    if (!sending) return;
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [sending, load]);

  const counts = useMemo(() => {
    const result: Record<Filter, number> = { all: 0, delivered: 0, bounced: 0, failed: 0, open: 0 };
    for (const r of detail?.recipients ?? []) {
      result.all += 1;
      result[kindOf(r)] += 1;
    }
    return result;
  }, [detail]);

  // Open on what needs attention first.
  const active: Filter = filter ?? (counts.failed > 0 ? "failed" : counts.bounced > 0 ? "bounced" : "all");

  const lists = useMemo(() => {
    const seen = new Map<string, number>();
    for (const r of detail?.recipients ?? []) {
      const key = r.list_id_short ?? "";
      seen.set(key, (seen.get(key) ?? 0) + 1);
    }
    return [...seen.entries()].sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : collator.compare(a, b)));
  }, [detail]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = (detail?.recipients ?? []).filter(
      (r) =>
        (active === "all" || kindOf(r) === active) &&
        (listFilter === null || (r.list_id_short ?? "") === listFilter) &&
        (!q ||
          r.address.toLowerCase().includes(q) ||
          (r.list_id_short ?? "").toLowerCase().includes(q) ||
          reasonOf(r).toLowerCase().includes(q)),
    );
    const sign = sortDir === "asc" ? 1 : -1;
    // Address breaks ties, so equal values keep a stable, readable order.
    return filtered
      .map((r) => ({ r, value: sortValue(r, sortKey) }))
      .sort((a, b) => {
        const primary = compareValues(a.value, b.value);
        if (primary !== 0) return a.value === null || b.value === null ? primary : sign * primary;
        return collator.compare(a.r.address, b.r.address);
      })
      .map(({ r }) => r);
  }, [detail, active, listFilter, query, sortKey, sortDir]);

  const items = useMemo((): Item[] => {
    if (groupBy === "none") return rows.map((r) => ({ kind: "row", r }));
    const groups = new Map<string, { label: string; rank: number | string; rows: Recipient[] }>();
    for (const r of rows) {
      const group = groupOf(r, groupBy);
      const entry = groups.get(group.id) ?? { label: group.label, rank: group.rank, rows: [] };
      entry.rows.push(r);
      groups.set(group.id, entry);
    }
    // Domains and codes: biggest group first; the rest by name or status order.
    const bySize = groupBy === "domain" || groupBy === "code";
    const ordered = [...groups.entries()].sort(([, a], [, b]) =>
      bySize
        ? b.rows.length - a.rows.length || collator.compare(a.label, b.label)
        : compareValues(a.rank, b.rank) || collator.compare(a.label, b.label),
    );
    const result: Item[] = [];
    for (const [id, group] of ordered) {
      const isCollapsed = collapsed[`${groupBy}:${id}`] ?? false;
      result.push({ kind: "group", id, label: group.label, count: group.rows.length, collapsed: isCollapsed });
      if (!isCollapsed) for (const r of group.rows) result.push({ kind: "row", r });
    }
    return result;
  }, [rows, groupBy, collapsed]);

  // A new view starts at the top with the first page.
  useEffect(() => {
    setShown(PAGE);
    scroller.current?.scrollTo({ top: 0 });
  }, [active, listFilter, query, sortKey, sortDir, groupBy]);

  // Endless scrolling: the next page loads when the end of the list comes into view.
  const more = items.length > shown;
  useEffect(() => {
    const target = sentinel.current;
    if (!more || !target) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) setShown((n) => n + PAGE);
      },
      { root: scroller.current, rootMargin: "600px 0px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [more, shown]);

  function toggleSort(key: SortKey) {
    const dir: SortDir = key === sortKey ? (sortDir === "asc" ? "desc" : "asc") : key === "time" ? "desc" : "asc";
    setSortKey(key);
    setSortDir(dir);
    store(SORT_STORE, key);
    store(`${SORT_STORE}.dir`, dir);
  }

  function chooseGroup(value: GroupKey) {
    setGroupBy(value);
    store(GROUP_STORE, value);
  }

  const groupItems = items.filter((item) => item.kind === "group");
  const allCollapsed = groupItems.length > 0 && groupItems.every((item) => item.collapsed);

  function setAllGroups(value: boolean) {
    setCollapsed((prev) => {
      const next = { ...prev };
      for (const item of groupItems) next[`${groupBy}:${item.id}`] = value;
      return next;
    });
  }

  function toggleGroup(id: string) {
    const key = `${groupBy}:${id}`;
    setCollapsed((prev) => ({ ...prev, [key]: !(prev[key] ?? false) }));
  }

  async function act(recipientId: string, action: "retry" | "ack" | "cancel") {
    setBusy(recipientId);
    try {
      await deliveryAction(recipientId, action);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Aktion fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  }

  async function retryAll() {
    setBusy("bulk");
    try {
      const { count } = await retryFailed(["failed", "rejected"], id);
      setNotice(`${count} ${count === 1 ? "Kopie" : "Kopien"} wieder in der Queue.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Erneut senden fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  }

  if (!detail) {
    return (
      <div className="mx-auto w-full max-w-5xl px-4 py-4">
        <Link to="/queue" className="text-xs text-accent hover:underline">
          ← Zurück zur Queue
        </Link>
        {error ? (
          <p className="mt-3 text-sm text-danger">{error}</p>
        ) : (
          <div className="mt-3 h-28 animate-pulse rounded-lg bg-surface-subtle" />
        )}
      </div>
    );
  }

  const { message } = detail;
  const s = message.stats;
  const retryable = detail.recipients.filter((r) => r.state === "failed" || r.state === "rejected").length;

  return (
    <div className="mx-auto w-full max-w-5xl space-y-4 px-4 py-4">
      <Link to="/queue" className="text-xs text-accent hover:underline">
        ← Zurück zur Queue
      </Link>
      {error ? <p className="text-sm text-danger">{error}</p> : null}

      <section className="space-y-3 rounded-lg border border-hairline bg-surface p-3">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
            <Badge tone={typeTone(detail.event_type)}>{EVENT_TYPE_LABEL[detail.event_type]}</Badge>
            <Link to={`/events/${detail.event_id}`} className="hover:text-ink hover:underline">
              {detail.event_title || "Ohne Titel"}
            </Link>
            <span>· {MESSAGE_PHASE_LABEL[message.phase] ?? message.phase}</span>
          </div>
          <h1 className="text-lg font-semibold">{message.subject || "Ohne Betreff"}</h1>
          <p className="text-xs text-muted">
            Gesendet {formatDateTime(message.sent_at)} · an {fmt(s.total)} Empfänger
            {sending ? " · wird noch gesendet" : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm">
          <span>
            <strong className="tabular-nums text-green">{fmt(s.delivered)}</strong> zugestellt (
            {formatPercent(s.delivered, s.total)})
          </span>
          <span>
            <strong className={cn("tabular-nums", failedCount(s) > 0 ? "text-danger" : "text-muted")}>
              {fmt(failedCount(s))}
            </strong>{" "}
            fehlgeschlagen
          </span>
          <span>
            <strong className={cn("tabular-nums", s.bounced > 0 ? "text-amber" : "text-muted")}>
              {fmt(s.bounced)}
            </strong>{" "}
            Rückläufer
          </span>
          {s.queued > 0 ? (
            <span>
              <strong className="tabular-nums">{fmt(s.queued)}</strong> noch offen
            </span>
          ) : null}
        </div>
        <DeliveryStatsBar stats={s} size="lg" />
        <DeliveryStatsLegend />
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter">
            {FILTERS.map((item) => (
              <button
                key={item.id}
                type="button"
                aria-pressed={active === item.id}
                disabled={item.id !== "all" && counts[item.id] === 0}
                onClick={() => setFilter(item.id)}
                className={cn(
                  "rounded-md border px-2 py-1 text-xs disabled:cursor-default disabled:opacity-40",
                  active === item.id
                    ? "border-accent bg-accent-dim text-accent"
                    : "border-hairline bg-surface hover:bg-surface-subtle",
                )}
              >
                {item.label} <strong className="tabular-nums">{fmt(counts[item.id])}</strong>
              </button>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {lists.length > 1 ? (
              <select
                value={listFilter ?? "*"}
                onChange={(e) => setListFilter(e.target.value === "*" ? null : e.target.value)}
                aria-label="Liste"
                className="rounded-md border border-field-border bg-surface px-2 py-1 text-xs"
              >
                <option value="*">Alle Listen</option>
                {lists.map(([name, n]) => (
                  <option key={name} value={name}>
                    {name || "ohne Liste"} ({fmt(n)})
                  </option>
                ))}
              </select>
            ) : null}
            <label className="flex items-center gap-1 text-xs text-muted">
              Gruppieren
              <select
                value={groupBy}
                onChange={(e) => chooseGroup(e.target.value as GroupKey)}
                className="rounded-md border border-field-border bg-surface px-2 py-1 text-xs text-ink"
              >
                {GROUPS.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.label}
                  </option>
                ))}
              </select>
            </label>
            {groupItems.length > 1 ? (
              <Button size="sm" variant="ghost" onClick={() => setAllGroups(!allCollapsed)}>
                {allCollapsed ? "Alle aufklappen" : "Alle zuklappen"}
              </Button>
            ) : null}
            <input
              id="recipient-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Adresse, Liste, Fehlertext …"
              aria-label="Empfänger suchen"
              className="w-56 max-w-full rounded-md border border-field-border bg-surface px-2 py-1 text-xs"
            />
            <Button
              size="sm"
              disabled={rows.length === 0}
              onClick={() => downloadCsv(rows, `empfaenger-${message.id.slice(0, 8)}-${active}.csv`)}
            >
              CSV
            </Button>
            {retryable > 0 ? (
              <Button size="sm" variant="primary" disabled={busy !== null} onClick={() => void retryAll()}>
                {retryable} erneut senden
              </Button>
            ) : null}
          </div>
        </div>
        {notice ? <p className="text-xs text-muted">{notice}</p> : null}
        {retryable > 0 && counts.failed > retryable ? (
          <p className="text-[11px] text-muted">
            „Unbekannt“ wird nicht gesammelt wiederholt, sondern je Empfänger entschieden.
          </p>
        ) : null}

        {rows.length === 0 ? (
          <p className="py-4 text-center text-xs text-muted">Keine Empfänger für diesen Filter.</p>
        ) : (
          <div
            ref={scroller}
            className="max-h-[calc(100vh-10rem)] overflow-auto rounded-lg border border-hairline"
          >
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 z-10 bg-surface-subtle text-muted [&_th]:shadow-[inset_0_-1px_0_var(--color-hairline)]">
                <tr>
                  {COLUMNS.map((col) => {
                    const current = sortKey === col.key;
                    return (
                      <th
                        key={col.key}
                        aria-sort={current ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
                        className="whitespace-nowrap px-3 py-2 font-medium"
                      >
                        <button
                          type="button"
                          onClick={() => toggleSort(col.key)}
                          className={cn("inline-flex items-center gap-1 hover:text-ink", current && "text-ink")}
                        >
                          {col.label}
                          <span aria-hidden className={cn("text-[9px]", !current && "opacity-30")}>
                            {current && sortDir === "desc" ? "▼" : "▲"}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                  <th className="px-3 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {items.slice(0, shown).map((item) => {
                  if (item.kind === "group") {
                    return (
                      <tr key={`g:${item.id}`} className="border-t border-hairline bg-surface-subtle">
                        <td colSpan={COLUMNS.length + 1} className="px-3 py-1.5">
                          <button
                            type="button"
                            onClick={() => toggleGroup(item.id)}
                            aria-expanded={!item.collapsed}
                            className="flex w-full items-center gap-2 text-left font-medium"
                          >
                            <span
                              className={cn(
                                "text-[10px] text-muted transition-transform motion-reduce:transition-none",
                                !item.collapsed && "rotate-90",
                              )}
                            >
                              ▶
                            </span>
                            <span className={groupBy === "domain" || groupBy === "list" ? "font-mono" : undefined}>
                              {item.label}
                            </span>
                            <span className="tabular-nums font-normal text-muted">{fmt(item.count)}</span>
                          </button>
                        </td>
                      </tr>
                    );
                  }
                  const r = item.r;
                  const view = stateView(r);
                  const when = timeOf(r);
                  return (
                    <tr key={r.id} className="border-t border-hairline align-top">
                      <td className="px-3 py-2 font-mono">{r.address}</td>
                      <td className="px-3 py-2 font-mono text-muted">{r.domain || "—"}</td>
                      <td className="px-3 py-2 text-muted">{r.list_id_short ?? "—"}</td>
                      <td className="px-3 py-2 uppercase text-muted">{r.language || "—"}</td>
                      <td className="whitespace-nowrap px-3 py-2">
                        <span className="inline-flex items-center gap-1.5">
                          <span className={cn("inline-block h-2 w-2 rounded-full", view.dot)} />
                          {view.label}
                        </span>
                        {r.bounced_at ? (
                          <div className="text-[11px] text-muted">{bounceCategoryLabel(r.bounce_category)}</div>
                        ) : null}
                        {r.attempts > 1 ? (
                          <div className="text-[11px] text-muted">{r.attempts} Versuche</div>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 tabular-nums text-muted">
                        {when ? formatDateTime(when) : "—"}
                      </td>
                      <td className="max-w-md px-3 py-2">
                        <span className="break-words font-mono text-[11px] text-muted">{reasonOf(r)}</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        {r.state === "failed" || r.state === "rejected" ? (
                          <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => void act(r.id, "retry")}>
                            Erneut senden
                          </Button>
                        ) : null}
                        {r.state === "unknown" ? (
                          <span className="flex justify-end gap-1">
                            <Button size="sm" disabled={busy !== null} onClick={() => void act(r.id, "retry")}>
                              Erneut
                            </Button>
                            <Button size="sm" variant="ghost" disabled={busy !== null} onClick={() => void act(r.id, "ack")}>
                              War draußen
                            </Button>
                          </span>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {more ? (
              <div ref={sentinel} className="px-3 py-3 text-center text-[11px] text-muted">
                Lade weitere …
              </div>
            ) : null}
          </div>
        )}
        <p className="text-[11px] text-muted">
          {fmt(rows.length)} Empfänger in dieser Ansicht. Spaltenkopf
          klicken zum Sortieren, noch einmal klicken dreht die Reihenfolge.
        </p>
        <p className="text-[11px] text-muted">
          „Zeit“ ist bei zugestellten Kopien die Annahme durch den Mailserver, bei Rückläufern der Eingang
          des Bounce-Reports, bei offenen der nächste Versuch.
        </p>
      </section>
    </div>
  );
}
